import React from 'react';
import { cn } from '../../lib/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options?: SelectOption[];
  placeholder?: string;
  containerClassName?: string;
}

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, className, containerClassName, id, children, ...props }, ref) => {
    const fieldId = id || props.name;
    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {label && (
          <label htmlFor={fieldId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={fieldId}
            className={cn(
              'h-11 w-full appearance-none rounded-lg border bg-surface-1 pl-3 pr-9 text-base text-foreground',
              'transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-60',
              error ? 'border-danger focus-visible:ring-danger/30' : 'border-border hover:border-border-strong',
              className
            )}
            aria-invalid={!!error}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((o) => (
                  <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}
                  </option>
                ))
              : children}
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-subtle">
            <ChevronDown />
          </span>
        </div>
        {error ? (
          <span className="text-sm text-danger">{error}</span>
        ) : hint ? (
          <span className="text-sm text-foreground-muted">{hint}</span>
        ) : null}
      </div>
    );
  }
);

Select.displayName = 'Select';
